import React, { useState, useRef } from 'react';
import ImageUploadService from '../services/ImageUploadService';

function ImageUpload({ 
  onUploadComplete, 
  onUploadError, 
  userId, 
  type = 'profile',
  workshopId = null,
  multiple = false,
  maxFiles = 5,
  className = '',
  placeholder = 'Click to upload or drag and drop'
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    
    if (multiple && fileArray.length > maxFiles) {
      onUploadError(`Please select no more than ${maxFiles} files`);
      return;
    }
    
    try {
      fileArray.forEach(file => ImageUploadService.validateImageFile(file));
    } catch (error) {
      onUploadError(error.message);
      return;
    }
    
    setSelectedFiles(fileArray);
    
    if (!multiple && fileArray.length > 0) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(fileArray[0]);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    
    try {
      let result;
      
      if (type === 'profile') {
        result = await ImageUploadService.uploadProfilePicture(userId, selectedFiles[0]);
      }
      
      onUploadComplete(result);
      
      setSelectedFiles([]);
      setPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      onUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ${
          isDragging
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        } ${uploading ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          multiple={multiple}
          onChange={handleFileInputChange}
          className="hidden"
        />
        
        {uploading ? (
          <div className="space-y-2">
            <div className="w-8 h-8 border-2 border-gray-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-sm text-gray-900">Uploading...</p>
          </div>
        ) : (
          <div className="space-y-2">
            <svg className="w-8 h-8 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-gray-900">{placeholder}</p>
            <p className="text-xs text-gray-500">
              JPEG, PNG, WebP • Max 10MB
            </p>
          </div>
        )}
      </div>

      {preview && (
        <div className="relative">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-full h-48 object-cover rounded-lg border border-gray-300"
          />
          <button
            onClick={() => {
              setPreview(null);
              setSelectedFiles([]);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-red-600"
          >
            ×
          </button>
        </div>
      )}

      {selectedFiles.length > 0 && !uploading && (
        <button
          onClick={handleUpload}
          className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700"
        >
          Upload Image
        </button>
      )}
    </div>
  );
}

export default ImageUpload;
